import Templater from '@hc-frontend/shared-assets/src/js/utils/dom/Templater'
import querySelectorAll from 'utils/DOM/querySelectorAll'
import { REQUIRED_ERROR_MESSAGE } from 'messages'
import setErrorMessage from './setErrorMessage'
import innerHTML from 'utils/DOM/innerHTML'
import getById from 'utils/DOM/getById'
import updateDOM from './updateDOM'
import { MULTI_CARRIER_ENABLED } from 'core/constants/environment'
import store from 'utils/store/withMiddleware'

const optionsPeriod = [
  {
    id: 'period-90',
    value: '90',
    label: '90 Days',
    help: 'Pivot Health offers you the opportunity to apply for one 90-day policy which gives you nearly 3-months of coverage.'
  },
  {
    id: 'period-180',
    value: '180',
    label: '180 Days',
    help: 'Need coverage for up to 180 days? May be great for individuals needing coverage for a shorter period of time.'
  },
  {
    id: 'period-330',
    value: '330',
    label: '330 Days',
    help: 'If you need health insurance coverage for more than a few months, 330-days of short term health insurance can take you the distance. May be great for individuals seeking a longer-term solution.'
  },
  {
    id: 'period-364',
    value: '364',
    label: '364 Days',
    help: 'If you need health insurance coverage for more than a few months, 364-days of short term health insurance can take you the distance. May be great for individuals seeking a longer-term solution.'
  },
  {
    id: 'period-4',
    value: '4',
    label: '<div style="font-weight: 400;">Up to 4 months</div>',
    help: 'Up to 4 months of coverage is ideal for those between jobs or needing temporary insurance, offering affordable protection during transitions.'
  },
  {
    id: 'period-8',
    value: '8',
    label: '<div style="font-weight: 700;">Up to 8 months</div>',
    help: '<b>The 1-2-8 Solution offers up to 8 months of coverage through two A-rated carriers, each providing a preselected plan for 4 months, ensuring continuous protection without gaps.</b>'
  },
  {
    id: 'period-12',
    value: '12',
    label: '<div style="font-weight: 700;">Up to 12 months</div>',
    help: '<b>The 1-3-12 Solution offers up to 12 months of coverage through three A-rated carriers, each providing a preselected plan for 4 months, ensuring continuous protection without gaps.</b>'
  }
]

type OptionPeriod = typeof optionsPeriod[number]

function buildMessage(period) {
  return `<p>${
    period.value === '90' ? '90 days coverage' : '180 days coverage'
  } <a href="javascript:void(0)" data-toggle="modal" data-target="#learMoreModal">(Learn more)</a></p>`
}

const getPeriodOptions = (storage, state) => {
  // For non multi carrier functionality
  if (!MULTI_CARRIER_ENABLED)
    return optionsPeriod.filter(
      option => state.options.coverage && state.options.coverage.includes(+option.value)
    )

  // For multi carrier functionality
  const options: Array<OptionPeriod> = []
  if (state.options.coverage.includes(90))
    options.push(optionsPeriod.find(option => option.id === 'period-90') as OptionPeriod)

  // If multicarrier service return false it is necessary to check the coverage previous result. So far 120 days equals 4 months
  if (storage.eligibilityMonths >= 4 || state.options.coverage.includes(120))
    options.push(optionsPeriod.find(option => option.id === 'period-4') as OptionPeriod)

  if (storage.eligibilityMonths === 8)
    options.push(optionsPeriod.find(option => option.id === 'period-8') as OptionPeriod)

  if (storage.eligibilityMonths === 12)
    options.push(optionsPeriod.find(option => option.id === 'period-12') as OptionPeriod)

  return options
}

let onlyOneOption
function render(state: ControllerState) {
  const options = getPeriodOptions(store.getState(), state)

  if (options.length === 0) {
    return undefined
  }

  // Clean the DOM before render
  innerHTML('#containerPeriod', '')

  if (options.length === 1) {
    onlyOneOption = options[0]
    if (!MULTI_CARRIER_ENABLED) innerHTML('#containerPeriod', buildMessage(onlyOneOption))
  } else {
    const blueTemplate = Templater(getById('template-period').innerText)
    const orangeTemplate = Templater(getById('template-period-orange').innerText)
    onlyOneOption = undefined
    innerHTML(
      '#containerPeriod',
      options
        .map(option => {
          if (option.id === 'period-12' || option.id === 'period-8')
            return orangeTemplate(option)
          else return blueTemplate(option)
        })
        .join('')
    )
    state.inputs = querySelectorAll(`[name=${state.name}]`) || []

    state.inputs[state.inputs.length - 1].checked = true
  }

  state.error = false
  state.valid = true
  updateDOM(state)
}

type ControllerState = {
  name: string
  wrap: HTMLElement
  errorWrap: HTMLElement
  valid: boolean
  error: boolean
  value: string
  inputs: any[] | NodeListOf<Element>
  options: any
  optional: boolean
}

function buildControllerPeriod(name) {
  const state = {
    name,
    wrap: getById(`wrap-${name}`),
    errorWrap: getById(`error-${name}`),
    valid: false,
    error: false,
    value: '',
    inputs: [],
    optional: false
  }

  $(state.wrap).on('change', 'input', () => {
    if (state.error) {
      state.error = false
      updateDOM(state)
    }
  })

  return {
    setValue: ({ data, period }) => {
      state.value = `${period}`
      state.options = data

      render(state)
    },
    getStatus: () => {
      if (state.optional) {
        return {
          valid: true,
          value: ''
        }
      }

      if (onlyOneOption) {
        return {
          valid: true,
          value: onlyOneOption.value
        }
      }

      for (let index = 0; index < state.inputs.length; index++) {
        const input = state.inputs[index]
        if (input.checked) {
          return {
            valid: true,
            value: input.value
          }
        }
      }

      return {
        valid: false,
        value: ''
      }
    },
    makeOptional: (isOptional = true) => {
      state.optional = isOptional
    },
    validate: () => {
      if (onlyOneOption || state.optional) {
        state.valid = true
        state.error = false
      } else {
        state.valid = false
        for (let index = 0; index < state.inputs.length; index++) {
          if (state.inputs[index].checked) {
            state.valid = true
            state.error = false
            break
          }
        }
      }

      if (!state.valid) {
        setErrorMessage(state, REQUIRED_ERROR_MESSAGE)
      }
      updateDOM(state)
    }
  }
}

export default buildControllerPeriod
